// The Supporting Document control in Raise Service Request (v1.3 ticket 07, RCMC-01), also the attachment control in a
// Service Request's Add new comment (ticket 21, Q34): `id` and `label` tell the two apart.
//
// v1.2 had a clickable div over a `hidden` file input: no Tab stop, no name, no stated limits. Here the
// native file input itself takes focus and is named by its <label>, which is styled as the old button
// (SPEC "Raise Service Request"); the browser opens the picker on Enter or Space and on a click on the label.
// The input is visually hidden, not `hidden`, so it stays in the Tab order; its focus ring is drawn on the
// label. Its description is the chosen file's name, the limits, and any error, in that order.
//
// Presentational: the form owns the chosen file and the error (they are reset with the form) and decides
// what each choice means (ServiceRequest.jsx, ServiceRequestDetails/Comments/Comments.jsx).
import { IoCloudUpload } from "react-icons/io5";
import { SUPPORTING_DOCUMENT_ACCEPT, SUPPORTING_DOCUMENT_HINT } from "../../Util/supportingDocument";
import styles from "./ServiceRequest.module.css";

export const UPLOAD_ID = "supporting-document";

/**
 * @param {object} props
 * @param {string} [props.id=UPLOAD_ID]  the input's id (its name, hint and error ids derive from it)
 * @param {string} [props.label="Upload supporting document"]  the visible label, which names the input
 * @param {React.RefObject<HTMLInputElement>} props.inputRef  the file input (the form clears and focuses it)
 * @param {File|""} props.file        the chosen file, or "" for none
 * @param {string} [props.error]      why the last file was refused
 * @param {(file: File|undefined) => void} props.onChoose  the picker's file (undefined when cancelled)
 * @param {() => void} props.onRemove the Remove button was pressed
 */
const SupportingDocumentUpload = ({ id = UPLOAD_ID, label = "Upload supporting document", inputRef, file, error, onChoose, onRemove }) => {
  const nameId = `${id}-name`;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [file && nameId, hintId, error && errorId].filter(Boolean).join(" ");

  return (
    <div className={styles["dialog-field"]}>
      <div className={styles["upload-row"]}>
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={SUPPORTING_DOCUMENT_ACCEPT}
          className={styles["upload-input"]}
          aria-describedby={describedBy}
          aria-invalid={error ? "true" : undefined}
          onChange={(e) => onChoose(e.target.files[0])}
        />
        <label htmlFor={id} className={styles["upload-label"]}>
          <IoCloudUpload aria-hidden="true" className={styles["upload-icon"]} />
          {label}
        </label>
        {file && <span id={nameId} className={styles["upload-file-name"]}>{file.name}</span>}
        {/* The visible "Remove" starts the name, so voice control matches it (2.5.3); the name says which file */}
        {file && (
          <button type="button" className={styles["upload-remove"]} aria-label={`Remove ${file.name}`} onClick={onRemove}>
            Remove
          </button>
        )}
      </div>
      <div id={hintId} className={styles["upload-hint"]}>{SUPPORTING_DOCUMENT_HINT}</div>
      {error && <div id={errorId} className={styles["field-error"]}>{error}</div>}
    </div>
  );
};

export default SupportingDocumentUpload;
